import { useInfiniteQuery } from 'react-query';
import { useContext } from 'react';

import {
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
  infiniteEmptyResults,
} from 'data_sources/constants';
import { AppContext } from 'views/App/context';

import type { AccreditationVersion } from '../../../_types/awb';
import type { PaginatedApiCollectionResponse } from 'types/api_response';

export async function getAccreditationVersions<T extends AccreditationVersion>(
  orgId: string,
  accreditationBodyId: string,
  params = {},
  signal?: AbortSignal,
  useRUIData = false
): Promise<PaginatedApiCollectionResponse<T>> {
  // Use non-org-specific API when awbRUIData feature is enabled
  const url = useRUIData
    ? `${baseApiUrl()}/awb/accreditation-body/v1/accreditation-bodies/${accreditationBodyId}/versions`
    : `${baseApiUrl()}/awb/accreditation-body/v1/organizations/${orgId}/accreditation-bodies/${accreditationBodyId}/versions`;

  const paramString = createParamString(params);

  const fullUrl = `${url}?${paramString}`;

  const data: PaginatedApiCollectionResponse<T> = await fetcher({
    url: fullUrl,
    method: 'get',
    options: { signal },
  });

  return data as PaginatedApiCollectionResponse<T>;
}

export default function useAccreditationVersions<T extends AccreditationVersion>(
  orgId: string,
  accreditationBodyId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { isFeatureEnabled } = useContext(AppContext);
  const useRUIData = isFeatureEnabled('awbRUIData');

  return useInfiniteQuery<PaginatedApiCollectionResponse<T>, Error>(
    ['accreditation_versions', orgId, accreditationBodyId, params, useRUIData],
    async ({ pageParam, signal }): Promise<PaginatedApiCollectionResponse<T>> => {
      const combinedParams = {
        ...params,
        nextToken: pageParam,
      };
      return getAccreditationVersions<T>(orgId, accreditationBodyId, combinedParams, signal, useRUIData);
    },
    {
      ...options,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.nextToken && lastPage.content.length > 0) {
          return lastPage.nextToken;
        }
      },
    }
  );
}
